import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Dialog, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Button } from 'components/UI';
import { Fragment, FunctionComponent, useState } from 'react';
import { wbp } from 'Theme';

type SelectionType = 'single' | 'all';

export interface ConfirmRecurringRescheduleDialogProps {
    open: boolean;
    upcomingAppointments: number;
    zIndex?: number;
    className?: string;
    onCancel?: () => void;
    onUpdate?: (selection: SelectionType) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialog: {
            '& .MuiDialog-paper': {
                boxShadow: 'none',

                [theme.breakpoints.down('xs')]: {
                    margin: 0,
                    width: '100%',
                    height: '100%',
                    maxWidth: 'unset',
                    maxHeight: 'unset',
                    borderRadius: 0
                },
                [theme.breakpoints.up('sm')]: {
                    padding: 0,
                    minWidth: 465,
                    borderRadius: 30
                },
                [theme.breakpoints.up('xl')]: {
                    padding: 0,
                    minWidth: 550,
                    borderRadius: 30
                }
            },
            //Title
            '& .MuiDialogTitle-root': {
                paddingBottom: 0
            },
            // Body
            '& .MuiDialogContent-root': {
                padding: 0,
                width: '70%',
                display: 'flex',
                margin: '0px auto',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',

                [theme.breakpoints.up('sm')]: {
                    width: '100%'
                },
                [theme.breakpoints.down('sm')]: {
                    width: '100%'
                }
            },

            // Actions
            '& .MuiDialogActions-root': {
                gap: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 16px',

                [theme.breakpoints.up('sm')]: {
                    marginTop: 37,
                    borderTop: '1px solid #DDDDDD',
                    padding: '17px 30px'
                },
                [theme.breakpoints.down('md')]: {
                    '& > button': {
                        minWidth: '48%'
                    },
                    '& > button:last-child': {
                        marginLeft: 0
                    }
                }
            }
        },
        title: {
            color: 'black',
            fontFamily: 'Poppins',
            fontSize: '20px',
            fontWeight: 600,
            marginBottom: '16px'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        radiosContainer: {
            textAlign: 'center'
        },
        radioServices: {
            'display': 'flex',
            '& .MuiRadio-colorSecondary.Mui-checked': {
                color: '#222'
            },
            [theme.breakpoints.down('sm')]: {
                'paddingBottom': 15,
                '& .MuiFormControlLabel-root': {
                    marginLeft: 0
                },
                '& .MuiRadio-root': {
                    paddingTop: 7,
                    paddingLeft: 0,
                    paddingBottom: 7
                }
            }
        },
        button: {
            paddingLeft: '30px',
            paddingRight: '30px',
            height: '50px',
            borderRadius: '50px',
            [theme.breakpoints.down('sm')]: {
                width: '250px'
            },
            [theme.breakpoints.down(wbp)]: {
                width: '250px'
            },
            [theme.breakpoints.up(wbp)]: {
                width: '250px'
            },
            [theme.breakpoints.up('xl')]: {
                width: '270px'
            }
        },
        iconClose: {
            fontSize: 20,
            float: 'right',
            cursor: 'pointer'
        },
        radioItems: {
            fontSize: 14,
            [theme.breakpoints.up('md')]: {
                fontSize: 16
            }
        },
        marginTop: {
            marginTop: 10
        },
        linesContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginTop: 24,
            marginLeft: 90,
            marginRight: 90,
            maxWidth: '100%'
        },
        titleLine: {
            fontSize: 16,
            paddingLeft: 20
        },
        optionLine: {
            fontSize: 16,
            marginTop: 16,
            textAlign: 'left'
        },
        ul: {
            marginTop: 0
        }
    })
);

export const ConfirmRecurringRescheduleDialog: FunctionComponent<ConfirmRecurringRescheduleDialogProps> = props => {
    const classes = useStyles(props);

    const [updating, setUpdating] = useState(false);
    const [selection, setSelection] = useState<SelectionType>('single');

    const updateHandler = () => {
        setUpdating(true);
        props.onUpdate && props.onUpdate(selection);
    };

    return (
        <Dialog
            open={props.open}
            style={{ zIndex: props.zIndex ?? 10040 }}
            className={clsx(classes.dialog, props.className)}
            onClose={() => !updating && props.onCancel && props.onCancel()}
        >
            <DialogTitle>
                <FontAwesomeIcon
                    icon={faTimes}
                    className={classes.iconClose}
                    onClick={() => !updating && props.onCancel && props.onCancel()}
                />
            </DialogTitle>

            <DialogContent>
                <Typography className={classes.title}>Update Recurring Appointment</Typography>
                <Box className={clsx(classes.container, classes.marginTop)}>
                    <Box className={classes.radiosContainer}>
                        <RadioGroup
                            aria-label="appointments"
                            value={selection}
                            name="appointments"
                            className={classes.radioServices}
                            onChange={(event, value) => !updating && setSelection(value as SelectionType)}
                        >
                            <FormControlLabel
                                value={'single'}
                                control={<Radio />}
                                key={'single'}
                                label={
                                    <Fragment>
                                        <Typography className={classes.radioItems}>Only this appointment</Typography>
                                    </Fragment>
                                }
                            />
                            <FormControlLabel
                                value={'all'}
                                control={<Radio />}
                                key={'all'}
                                label={
                                    <Fragment>
                                        <Typography className={classes.radioItems}>
                                            {`This and all upcoming appointments`}
                                        </Typography>
                                    </Fragment>
                                }
                            />
                        </RadioGroup>
                    </Box>
                    {selection === 'all' && (
                        <Box className={classes.linesContainer}>
                            <Typography className={classes.titleLine}>
                                Making changes to future appointments will:
                            </Typography>
                            <ul className={classes.ul}>
                                <li>
                                    <Typography className={classes.optionLine}>
                                        End the current recurring series on this date.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography className={classes.optionLine}>
                                        Start a new recurring series beginning with this appointment.
                                    </Typography>
                                </li>
                            </ul>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    id="reschedule_cancel_button"
                    label="Cancel"
                    onClick={props.onCancel}
                    color="black"
                    className={classes.button}
                    disabled={updating}
                />
                <Button
                    id="reschedule_confirm_button"
                    label="Update"
                    onClick={updateHandler}
                    color="green"
                    className={classes.button}
                    loading={updating}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmRecurringRescheduleDialog;
